import React, {
	type ReactNode,
	type MouseEvent,
	type ComponentType,
	type RefObject,
	type KeyboardEvent,
} from 'react';
import { IconButton } from '@atlaskit/button/new';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import { Flex, Inline, xcss, Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export type InlineConfigButtonsProps<QueryParams> = {
	children: ReactNode;
	source?: string;
	editActionSubjectId?: string;
	deleteActionSubjectId?: string;
	showEditButton?: boolean;
	showDeleteButton?: boolean;
	isFocused: boolean;
	editLabelText?: string;
	deleteLabelText?: string;
	queryParams: QueryParams;
	onEditButtonClick?: (ev: MouseEvent<HTMLElement>) => void;
	onDeleteButtonClick?: (ev: MouseEvent<HTMLElement>) => void;
	// Remove this prop when cleaning up inline_config_a11y_improvements
	shouldStopPropagationOnClick?: boolean;
	// Remove this prop when cleaning up inline_config_a11y_improvements
	buttonRef?: RefObject<HTMLElement> | null;
	EditButtonWrapper?: ComponentType<{
		children: ReactNode;
		queryParams: QueryParams;
		onClick?: (ev: MouseEvent<HTMLElement>) => void;
	}>;
	DeleteButtonWrapper?: ComponentType<{
		children: ReactNode;
		queryParams: QueryParams;
	}>;
};

export const InlineConfigButtons = <QueryParams extends {}>({
	children,
	source,
	editActionSubjectId,
	deleteActionSubjectId,
	showEditButton,
	showDeleteButton,
	isFocused,
	EditButtonWrapper,
	DeleteButtonWrapper,
	queryParams,
	editLabelText,
	deleteLabelText,
	onEditButtonClick,
	onDeleteButtonClick,
	shouldStopPropagationOnClick = false,
	buttonRef = null,
}: InlineConfigButtonsProps<QueryParams>) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onFireRequiredAnalyticsForEditButtonClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			editActionSubjectId ?? '',
			{
				source,
			},
		);
	};

	const onFireRequiredAnalyticsForDeleteButtonClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			deleteActionSubjectId ?? '',
			{
				source,
			},
		);
	};

	const handleOnEditButtonMouseDown = (e: MouseEvent | React.MouseEvent) => {
		// fire event on left click only
		if (e.button === 0) {
			onFireRequiredAnalyticsForEditButtonClick();
		}
	};

	const handleOnDeleteButtonMouseDown = (e: MouseEvent | React.MouseEvent) => {
		// fire event on left click only
		if (e.button === 0) {
			onFireRequiredAnalyticsForDeleteButtonClick();
		}
	};

	const handleOnEditButtonKeyDown = (e: KeyboardEvent | React.KeyboardEvent) => {
		if (e.code === 'Enter' || e.code === 'Space') {
			// Always stop propagation when cleaning up inline_config_a11y_improvements
			shouldStopPropagationOnClick && e.stopPropagation();
			// Remove this line when cleaning up inline_config_a11y_improvements
			buttonRef?.current?.click();
			onFireRequiredAnalyticsForEditButtonClick();
		}
	};

	const handleOnDeleteButtonKeyDown = (e: KeyboardEvent | React.KeyboardEvent) => {
		if (e.code === 'Enter' || e.code === 'Space') {
			shouldStopPropagationOnClick && e.stopPropagation();
			onFireRequiredAnalyticsForDeleteButtonClick();
		}
	};

	const handleOnEditButtonClick = (e: MouseEvent<HTMLElement>) => {
		shouldStopPropagationOnClick && e.stopPropagation();
		onEditButtonClick?.(e);
	};

	const handleOnDeleteButtonClick = (e: MouseEvent<HTMLElement>) => {
		shouldStopPropagationOnClick && e.stopPropagation();
		onDeleteButtonClick?.(e);
	};

	const renderEditButton = () => {
		const iconButton = (
			<IconButton
				tabIndex={isFocused ? 0 : -1}
				isDisabled={!isFocused}
				appearance="subtle"
				onClick={handleOnEditButtonClick}
				// onClick is not working well with Select component and ModalEntryPointPressableTrigger
				// Therefore, we need to handle event analytics using mouseDown, touchEnd and keyDown
				onMouseDown={handleOnEditButtonMouseDown}
				onTouchEnd={onFireRequiredAnalyticsForEditButtonClick}
				onKeyDown={handleOnEditButtonKeyDown}
				icon={EditIcon}
				label={editLabelText ?? formatMessage(messages.editButtonText)}
				spacing="compact"
			/>
		);
		return <Box xcss={additionalMarginStyles}>{iconButton}</Box>;
	};

	return (
		<Flex direction="row" columnGap="space.050">
			<Inline xcss={leftColumnStyle}>{children}</Inline>

			<Inline xcss={isFocused ? rightColumnStyleVisible : rightColumnStyleHidden}>
				<Flex direction="row" columnGap="space.050">
					{showEditButton && EditButtonWrapper && (
						<EditButtonWrapper queryParams={queryParams}>
							<Tooltip content={editLabelText ?? formatMessage(messages.editButtonText)}>
								{renderEditButton()}
							</Tooltip>
						</EditButtonWrapper>
					)}
					{showDeleteButton && DeleteButtonWrapper && (
						<DeleteButtonWrapper queryParams={queryParams}>
							<Tooltip content={deleteLabelText ?? formatMessage(messages.deleteButtonText)}>
								<IconButton
									isDisabled={!isFocused}
									tabIndex={isFocused ? 0 : -1}
									appearance="subtle"
									onClick={handleOnDeleteButtonClick}
									// onClick is not working well with Select component and ModalEntryPointPressableTrigger
									// Therefore, we need to handle event analytics using mouseDown, touchEnd and keyDown
									onMouseDown={handleOnDeleteButtonMouseDown}
									onTouchEnd={onFireRequiredAnalyticsForDeleteButtonClick}
									onKeyDown={handleOnDeleteButtonKeyDown}
									icon={DeleteIcon}
									label={deleteLabelText ?? formatMessage(messages.deleteButtonText)}
								/>
							</Tooltip>
						</DeleteButtonWrapper>
					)}
				</Flex>
			</Inline>
		</Flex>
	);
};

const leftColumnStyle = xcss({
	wordBreak: 'break-word',
	flex: 1,
	alignItems: 'center',
});

const rightColumnStyleVisible = xcss({
	width: 'auto',
	height: 'auto',
});

const rightColumnStyleHidden = xcss({
	width: 'auto',
	height: 'auto',
	opacity: 0,
});

const additionalMarginStyles = xcss({
	margin: 'space.050',
});
